import { DateTime } from 'luxon'

// Tab state for the editors. The strip itself is <EditorTabs> (components/ui/EditorTabs.vue, JDEV-3208): bind it with
// tabList (editorTabs()), selectedTab, tabIdPrefix and tab(); the panes get tabPanel(). The old tabClass() for the
// hand-written <ul class="nav nav-tabs"> strips is gone.

const SEARCH_FOCUS_TRIES = 50

export default {
  data () {
    return {
      activeTab: 'general'
    }
  },
  computed: {
    validatedActiveTab() {
      return undefined
    },
    // The selected tab for <EditorTabs :selected="selectedTab"> (components/ui/EditorTabs.vue, JDEV-3208): the
    // one isTabActive() is true for.
    selectedTab() {
      return typeof this.validatedActiveTab !== 'undefined' ? this.validatedActiveTab : this.activeTab
    },
    // Unique per component instance: the tab element ids are `${tabIdPrefix}-tab-<tab>` (<EditorTabs :id-prefix>),
    // which the panels' aria-labelledby (tabPanel()) refers to. The panes' own ids (id="general") repeat across
    // editors, so they are not used for aria-controls.
    tabIdPrefix() {
      return 'tabs-' + this.$.uid
    }
  },
  methods: {
    // The tabs for <EditorTabs :tabs>: [{ id, label, hidden?, ... }], conditional tabs (isConditionalTab) disabled.
    editorTabs(tabs) {
      return tabs.map(t => (this.isConditionalTab(t.id) ? { ...t, disabled: true } : t))
    },
    // Attributes for a tab's pane: <div v-bind="tabPanel('general')" class="tab-pane active" id="general">
    tabPanel(tab) {
      return { role: 'tabpanel', 'aria-labelledby': `${this.tabIdPrefix}-tab-${tab}` }
    },
    isConditionalTab(tab) {
      return false
    },
    tab(tab) {
      if (this.isConditionalTab(tab)) {
        return
      }
      this.activeTab = tab
      if (typeof this.persist !== 'undefined') {
        this.persist('activeTab', tab, DateTime.now().plus({ hours: 2 }))
      }
    },
    isTabActive(tab) {
      if(typeof this.validatedActiveTab !== 'undefined') {
        return this.validatedActiveTab === tab
      }
      return this.activeTab === tab
    },
    // Scrolls to the field that a settings search result points to (initData.focus, a CSS
    // selector), focuses it and flashes it. The field appears only after the component has
    // loaded its data, so keep looking for a while.
    focusSearchTarget(selector) {
      clearInterval(this.searchFocusTimer)
      let tries = 0
      this.searchFocusTimer = setInterval(() => {
        tries++
        let el = null
        try {
          el = this.$el && this.$el.querySelector ? this.$el.querySelector(selector) : null
        } catch (e) {
          clearInterval(this.searchFocusTimer)
          return
        }
        if (!el || !el.offsetParent) {
          if (tries >= SEARCH_FOCUS_TRIES) {
            clearInterval(this.searchFocusTimer)
          }
          return
        }
        clearInterval(this.searchFocusTimer)
        // A closed accordion panel (its body is v-if'd away): open it by clicking its heading
        const heading = el.closest('.panel-heading') || (el.matches('.panel') ? el.querySelector(':scope > .panel-heading') : null)
        const panel = heading && heading.closest('.panel')
        const inputSelector = 'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
        if (panel && !panel.querySelector(':scope > .panel-collapse, :scope > * > .panel-collapse')) {
          heading.querySelector('[data-toggle="collapse"]')?.click()
          setTimeout(() => panel.querySelector('.panel-collapse')?.querySelector(inputSelector)?.focus({ preventScroll: true }), 400)
        }
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        const input = el.matches('input, select, textarea') ? el : el.querySelector(inputSelector)
        if (input) {
          input.focus({ preventScroll: true })
        }
        el.classList.add('settings-search-flash')
        setTimeout(() => el.classList.remove('settings-search-flash'), 2000)
      }, 100)
    },
    applyInitDataTab() {
      if (this.component?.initData?.activateTab) {
        this.tab(this.component.initData.activateTab)
      }
      if (this.component?.initData?.focus) {
        const selector = this.component.initData.focus
        // Only once, not again when the user comes back to the component
        delete this.component.initData.focus
        this.focusSearchTarget(selector)
      }
    }
  },
  activated () {
    this.applyInitDataTab()
  },
  mounted () {
    this.applyInitDataTab()
  },
  beforeUnmount () {
    clearInterval(this.searchFocusTimer)
  }
}